/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import withStyles from '@material-ui/core/styles/withStyles';
import {
    object, shape, string, arrayOf,
} from 'prop-types';
import Grid from '@material-ui/core/Grid';
import ReactMarkdown from 'react-markdown';
import loadable from '@loadable/component';
import SeoLeftRailBlock from './Partials/SeoLeftRailBlock';
import SeoRelatedArticlesBlock from './Partials/SeoRelatedArticlesBlock';
import GraphqlBreadcrumbs from '../GraphqlCommonComponents/GraphqlBreadcrumbs/GraphqlBreadcrumbs';
import customBreakpoints from '../../../helpers/customBreakpoints';
import { trackData } from '../../../helpers/tracking/common/commonTrackingHelpers';
import SeoBirthFlowersBlock from './SeoBirthFlowersBlock/SeoBirthFlowersBlock';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import SeoUniversalPicturesInfoBlock from './SeoUniversalPicturesInfoBlock/SeoUniversalPicturesInfoBlock';
import SeoPlantsAboutUs from './Partials/SeoPlantsAboutUs';
import SeoPlantingZone from './Partials/SeoPlantingZone';
import SeoPlantsFaqs from './Partials/SeoPlantsFaqs'; /* eslint-disable-line */
import SeoPlantsDelivery from './Partials/SeoPlantsDelivery';
import SeoLegalDisclaimers from './Partials/SeoLegalDisclaimers';
import SeoShippingInfo from './Partials/SeoShippingInfo';

const SeoInTheNews = loadable(() => import(/* webpackChunkName: "SeoInTheNews" */ './Partials/SeoInTheNews'));

const styles = (theme)  => ({
    container: {
        [theme.breakpoints.down('sm')]: {
            maxWidth: theme.palette.tiers.container.maxWidth.downSmall,
            margin: '0 auto',
        },
        [theme.breakpoints.down('md')]: {
            maxWidth: theme.palette.tiers.container.maxWidth.downMedium,
            padding: '15px',
        },
        [theme.breakpoints.up('md')]: {
            width: '100%',
            padding: '15px',
        },
        [theme.breakpoints.up('lg')]: {
            width: '100%',
            padding: '15px',
        },
    },
    articleBodyLeftRail: {
        fontSize: 14,
        '& strong': {
            display: 'block',
        },
        '& a': {
            color: theme.palette.colorPrimary,
        },
    },
    articleBody: {
        fontSize: 14,
        '& p': {
            paddingLeft: 40,
        },
        '& p:last-child': {
            textAlign: 'center',
            paddingLeft: 0,
        },
        '& p:nth-child(2)': {
            paddingLeft: 0,
        },
        '& p:nth-child(1)': {
            paddingLeft: 0,
        },
    },
    articleBodyAboutUs: {
        padding: '0 70px',
        fontSize: '20px',
        lineHeight: '35px',
        [theme.breakpoints.down('xs')]: {
            fontSize: '18px',
            lineHeight: '35px',
            padding: '0',
        },
        '& p:last-child': {
            textAlign: 'center',
            paddingLeft: 0,
        },
        '& p:nth-child(1)': {
            paddingLeft: 0,
        },
        '& p:nth-child(2)': {
            paddingLeft: 0,
            marginBottom: 0,
        },
        '& p:nth-child(3)': {
            padding: 12,
            margin: '0 30px',
            borderTop: `2px solid ${theme.palette.common?.black}`,
            borderBottom: `2px solid ${theme.palette.common?.black}`,
            [theme.breakpoints.down('xs')]: {
                margin: '0',
            },
        },
        '& p:nth-child(4)': {
            paddingLeft: 0,
            fontSize: '36px',
            lineHeight: '48px',
            color: theme.palette.primary?.main,
            [theme.breakpoints.down('xs')]: {
                fontSize: '26px',
            },
        },
    },
    header: {
        margin: '0px 0px 10px',
        fontWeight: 'normal',
        textAlign: 'center',
    },
    headerLeftRail: {
        margin: '10px 0px 10px',
        textAlign: 'center',
        fontWeight: 'bold',
    },
    heroImg: {
        width: '100%',
    },
    breadcrumb: {
        marginBottom: 10,
        paddingLeft: 30,
    },
    alignment: {
        display: 'flex',
    },
    heroBlock: {
        textAlign: 'center',
    },
    heroTitle: {
        margin: '0',
        color: theme.palette.flowerGift.topLogoText.color,
        fontFamily: 'Roboto serif',
        fontSize: '40px',
        fontWeight: '300',
        border: 'none',
        lineHeight: '1.2em',
    },
    heroCopy: {
        fontSize: '1.6em',
        fontWeight: 'bold',
        '& p': {
            marginTop: '10px',
        },
    },
    imageBlock: {
        width: '100%',
    },
    contentWrapper: {
        display: 'flex',
    },
    aboutUsContainer: {
        maxWidth: '960px',
        boxShadow: `0 0 10px ${theme.palette.common?.black}`,
        border: `25px solid ${theme.palette.common?.white}`,
        background: theme.palette.backgroundColor,
        margin: '0 auto',
        [theme.breakpoints.down('600')]: {
            maxWidth: '100%',
        },
        '& $contentWrapper': {
            textAlign: 'center',
            justifyContent: 'center',
        },
        '& $imageBlock': {
            width: '75%',
            margin: '0 auto',
        },
        '& img': {
            maxWidth: '100%',
            width: 'auto',
            objectFit: 'contain',
            verticalAlign: 'middle',
        },
        '& p': {
            whiteSpace: 'break-spaces',
        },
        '& $header': {
            fontSize: '35px',
            fontWeight: 'blod',
        },
    },
    appleChatContainer: {
        maxWidth: '1440px',
        margin: '0 auto',
        [theme.breakpoints.down('600')]: {
            maxWidth: '100%',
        },
        '& $imageBlock, & p': {
            lineHeight: 0,
            margin: 0,
        },
        '& $header, & $breadcrumb': {
            display: 'none',
        },
        '& img': {
            maxWidth: '100%',
            width: '100%',
        },
    },
    passportContainer: {
        maxWidth: '1440px',
        margin: '0 auto',
        '& $heroTitle': {
            width: '50%',
            fontSize: '20px',
            margin: '0 auto',
            fontWeight: 700,
            [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                width: '100%',
            },
        },
        [theme.breakpoints.down('600')]: {
            maxWidth: '100%',
        },
        '& $heroBlock, & $articleBody': {
            width: '70%',
            margin: '0 auto',
            [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                width: '95%',
            },
        },
        '& $articleBody': {
            '& p': {
                textAlign: 'left ',
                paddingLeft: 0,
            },
        },
        '& ol': {
            paddingLeft: '20px',
        },
        '& h2': {
            textAlign: 'center',
            fontSize: '18px',
        },
        '& $heroCopy': {
            fontSize: '12px',
            '& p': {
                textAlign: 'right',
            },
        },
    },
    flowersUSA: {
        '& $header': {
            color: theme.palette.primary.main,
            fontWeight: 200,
            textAlign: 'left',
            maxWidth: '66%',
            fontSize: 40,
            [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
                maxWidth: '100%',
            },
        },
        '& $contentWrapper': {
            backgroundColor: '#f3faf6',
            maxWidth: '1000px',
            margin: 'auto',
            padding: '25px',
            backgroundImage: 'url(https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltafdd6753c89bbd83/5ef504871ce00b649d2b557c/bkgd.jpg)',
            backgroundPosition: 'top 100px right 25px',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '60%',
            [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
                padding: '10px',
                backgroundImage: 'none',
            },
        },
        '& $articleBody': {
            display: 'flex',
            flexWrap: 'wrap',
            '& p': {
                width: '50%',
                margin: '0 0 25px 0',
                padding: '0 15px 15px 15px',
                border: `1px solid ${theme.palette.grey10}`,
                borderTop: 0,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                textAlign: 'left',
                [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
                    width: '100%',
                },
                '& a': {
                    width: '33%',
                    margin: '8px 0',
                    fontSize: 16,
                    color: theme.palette.black,
                    [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                        width: '50%',
                    },
                },
                '& strong': {
                    width: '100%',
                    fontSize: 16,
                    [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                        fontSize: 20,
                        margin: '10px 0',
                    },
                },
            },
            '& p:nth-of-type(1)': {
                width: '45%',
                fontSize: 20,
                border: 0,
                lineHeight: '30px',
                padding: 0,
                marginRight: '50%',
                [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
                    width: '100%',
                    marginRight: 0,
                },
            },
            '& p:nth-of-type(2)': {
                width: '51%',
                [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
                    width: '100%',
                },
            },
            '& p:nth-of-type(3), p:nth-of-type(4), p:nth-of-type(5)': {
                flex: '33.3%',
                padding: '0 10px',
                minHeight: '500px',
                [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                    flex: '100%',
                    minHeight: 'auto',
                },
                '& a': {
                    width: '50%',
                    [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                        width: '33%',
                    },
                    [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
                        width: '50%',
                    },
                },
            },
            '& p:nth-of-type(4), p:nth-of-type(5)': {
                borderLeft: 0,
                [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                    borderLeft: `1px solid ${theme.palette.grey10}`,
                    borderTop: `1px solid ${theme.palette.grey10}`,
                },
            },
            '& h2:nth-of-type(2)': {
                width: '100%',
                paddingBottom: '20px',
                [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                    border: 0,
                    borderBottom: `1px solid ${theme.palette.grey10}`,
                },
            },
            '& h2': {
                width: '51%',
                fontSize: 24,
                margin: 0,
                padding: 15,
                border: `1px solid ${theme.palette.grey10}`,
                borderBottom: 0,
                [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
                    width: '100%',
                },
            },
        },
    },
    termsAndPrivacy: {
        maxWidth: 980,
        margin: 'auto',
        '& h1': {
            [theme.breakpoints.down('321')]: {
                fontSize: '26px',
                lineHeight: '40px',
                letterSpacing: '-1px',
            },
        },
        '& $articleBody': {
            '& p': {
                textAlign: 'left',
                paddingLeft: 0,
            },
            '& img': {
                position: 'absolute',
                top: '0',
                right: '0',
                [theme.breakpoints.down('321')]: {
                    width: '120px',
                },
            },
        },
        '& $breadcrumb': {
            paddingLeft: 0,
        },
        '& $header': {
            textAlign: 'left',
        },
        '& $contentWrapper': {
            position: 'relative',
        },
    },
    faqPage: {
        '& h2': {
            textAlign: 'center',
        },
        '& $articleBody': {
            fontSize: 14,
            '& p': {
                paddingLeft: 0,
            },
            '& p:last-child': {
                textAlign: 'left',
            },
        },
    },
});

const SeoArticlesBlock = ({
    classes, data, brand, ssrDeviceType,
}) => {
    const location = useLocation();
    useEffect(() => {
        if (location.hash && location.hash === '#accessibility-statement') {
            if (document !== 'undefined') {
                const element = document.querySelector('#accessibility-statement');
                if (element) {
                    if (ssrDeviceType === 'desktop') {
                        element.scrollIntoView();
                    } else {
                        const headerOffset = 130;
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                        window.scrollTo({
                            top: offsetPosition,
                        });
                    }
                }
            }
        }
    }, []);
    // Persist data layer
    trackData({
        untData: data,
        action: 'track_seo_articles_data',
    });
    const {
        article_header = '', article_body = '', hero: { hero_image_desktop, title = '', copy = '' } = {}, related_articles = {}, left_rail = [], seo = {},
    } = data;
    const imageUrl = hero_image_desktop?.url || null;
    const filename = hero_image_desktop?.filename || 'banner';
    const  pageUrl = data?.url;
    if (pageUrl === '/universal-pictures-info')  return <SeoUniversalPicturesInfoBlock data={data} />;

    if (brand?.code === 'PLA') {
        switch (pageUrl) {
            case '/about-us':
                return <SeoPlantsAboutUs data={data} />;
            case '/privacy-policy':
            case '/disclaimer':
            case '/t-c':
                return <SeoLegalDisclaimers data={data} />;
            case '/plants-delivery':
                return <SeoPlantsDelivery data={data} />;
            case '/in-the-news':
                return <SeoInTheNews data={data} />;
            case '/faqs':
                return <SeoPlantsFaqs data={data} />;
            case '/planting-zone':
                return <SeoPlantingZone data={data} />;
            case '/shipping-info':
                return <SeoShippingInfo data={data} />;
            default:
                return null;
        }
    }

    const  loadCssUrlBase = () => {
        switch (pageUrl) {
            case '/about-us':
                return classes.aboutUsContainer;
            case '/apple-business-chat':
                return classes.appleChatContainer;
            case '/passport-terms-and-conditions':
                return classes.passportContainer;
            case '/flowers-usa':
                return classes.flowersUSA;
            case '/About-Us-Privacy-Policy':
            case '/About-Us-Terms-of-Use':
            case '/t-c':
                return classes.termsAndPrivacy;
            case '/faqs':
                return classes.faqPage;
            default:
                return null;
        }
    };
    if (pageUrl === '/birth-flowers') {
        return <SeoBirthFlowersBlock data={data} />;
    }

    let articleBodyClass = left_rail.length === 0 ? classes.articleBody : classes.articleBodyLeftRail;
    if (pageUrl === '/about-us') {
        articleBodyClass = classes.articleBodyAboutUs;
    } else if (left_rail.length === 0 && pageUrl !== '/about-us') {
        articleBodyClass = classes.articleBody;
    } else {
        articleBodyClass = classes.articleBodyLeftRail;
    }

    return (
        <>
            {
                seo?.seo_searchable
                    ? (
                        <GraphqlSeoHeadTags seoData={seo} alternativeLang="true" />
                    ) : null
            }
            <div className={`${classes.container} ${loadCssUrlBase()}`}>
                <div className={classes.breadcrumb}>
                    <GraphqlBreadcrumbs breadCrumbArray={seo?.breadcrumb} />
                </div>
                <Grid className={classes.alignment}>
                    {(left_rail.length > 0 && pageUrl !== '/about-us') ? <SeoLeftRailBlock data={left_rail} pageUrl={pageUrl} /> : null}
                    <Grid container className={classes.contentWrapper}>
                        <Grid item xs={12} sm={left_rail && left_rail.length === 0 ? 12 : 9} md={left_rail && left_rail.length === 0 ? 12 : 9} lg={left_rail && left_rail.length === 0 ? 12 : 9}>
                            {!title || !copy
                                ? (
                                    <>
                                        { (title || article_header) && <h1 className={classes.header}>{title || article_header}</h1> }
                                        { imageUrl && <div className={classes.imageBlock}><img className={classes.heroImg} src={imageUrl} alt={filename} /></div>}
                                    </>
                                )
                                :  (
                                    <>
                                        <Grid className={classes.heroBlock}>
                                            <h1 className={classes.heroTitle}>{title}</h1>
                                            <ReactMarkdown
                                                source={copy}
                                                className={classes.heroCopy}
                                            />
                                        </Grid>
                                        { imageUrl && <img className={classes.heroImg} src={imageUrl} alt={filename} />}
                                        <h1 className={classes.headerLeftRail}>{article_header}</h1>
                                    </>
                                )}

                            <ReactMarkdown
                                escapeHtml={false}
                                source={article_body}
                                className={articleBodyClass}
                            />
                            {related_articles?.articles.length > 0
                                ?  <SeoRelatedArticlesBlock data={related_articles} />
                                : null}
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        </>
    );
};

SeoArticlesBlock.propTypes = {
    classes: object.isRequired,
    brand: object.isRequired,
    data: shape({
        article_header: string.isRequired,
        article_body: string.isRequired,
        hero: shape({
            hero_image_desktop: shape({
                url: string.isRequired,
                filename: string.isRequired,
            }),
        }).isRequired,
        related_articles: shape({
            articles: arrayOf(shape({
                image: object.isRequired,
                link: object.isRequired,
            })).isRequired,
            header: string.isRequired,
        }).isRequired,
        left_rail: arrayOf(shape({
            header: string.isRequired,
            link: arrayOf(shape({
                title: string.isRequired,
                href: string.isRequired,
            })).isRequired,
        })).isRequired,
    }).isRequired,
    ssrDeviceType: string.isRequired,
};
export default withStyles(styles)(SeoArticlesBlock);
